import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Notification from '@this/src/notification';
import { appendScript } from '@this/src/util';
import { MESSAGE_TYPES } from '../types/messages';
import type { ChatMessage, PusherPayload } from '../types/messages';
import { fetchAiMessages, sendAiMessage } from '../api';
import type { RecentMessagePayload } from '../api';
import { createArrangementConfirmation } from '../api/arrangementConfirmation';
import { createArrangement } from '../api/arrangement';
import { useDebugLog } from '../debug';
import { toChatMessage } from '../utils/messageNormalizer';

const tripAiMessageEventName = 'ai_message.created';
const buildTripAiChannelName = (prefix: string, tripId: string) => `${prefix || ''}trip_ai_messages_${tripId}`;

export type ProposalNotification = {
  receivedAt: number;
};

type Options = {
  tripId: string;
  pusherKey: string;
  pusherChannelPrefix: string;
  enabled?: boolean;
  initialMessages?: ChatMessage[];
  onProposalReceived?: (notification: ProposalNotification) => void;
  onReservationFormReceived: () => void;
  onScrollToBottom?: () => void;
};

type UseAiMessagesResult = {
  messages: ChatMessage[];
  inputValue: string;
  sending: boolean;
  canSend: boolean;
  handleInputChange: (value: string) => void;
  handleSend: () => Promise<void>;
  contextLength: number;
  setContextLength: (value: number) => void;
  disableReservationInfoForm: () => void;
  handleArrangementRequest: () => Promise<boolean>;
  isRequestingArrangement: boolean;
  isArrangementRequested: boolean;
  inputDisabled: boolean;
  proposalChangeDisabled: boolean;
  isReservationInfoFormEditable: boolean;
};

export const useAiMessages = ({
  tripId,
  pusherKey,
  pusherChannelPrefix,
  enabled = true,
  initialMessages = [],
  onProposalReceived,
  onReservationFormReceived,
  onScrollToBottom
}: Options): UseAiMessagesResult => {
  const debugLog = useDebugLog();
  const debugLogRef = useRef(debugLog);
  debugLogRef.current = debugLog;
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [contextLength, setContextLength] = useState(20);
  const [isRequestingArrangement, setIsRequestingArrangement] = useState(false);
  const [isArrangementRequested, setIsArrangementRequested] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [proposalChangeDisabled, setProposalChangeDisabled] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pusherRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channelRef = useRef<any>(null);
  const bindHandlerRef = useRef<((payload: PusherPayload) => void) | null>(null);
  const hasFetchedRef = useRef(false);
  const prevTripIdRef = useRef(tripId);

  useEffect(() => {
    if (prevTripIdRef.current !== tripId) {
      hasFetchedRef.current = false;
      prevTripIdRef.current = tripId;
    }
  }, [tripId]);

  useEffect(() => {
    if (!enabled || hasFetchedRef.current) {
      return undefined;
    }

    hasFetchedRef.current = true;
    let mounted = true;
    const loadMessages = async () => {
      const startTime = Date.now();
      try {
        const history = await fetchAiMessages(tripId);
        if (!mounted) return;
        debugLogRef.current.logApi('GET', `/trips/${tripId}/ai_messages.json`, {
          status: 200,
          duration: Date.now() - startTime,
          response: { messageCount: history.length }
        });
        if (mounted) {
          setMessages(prev => {
            if (prev.length === 0) return history;
            const existingTexts = new Set(history.map(m => m.text));
            const newLocalMessages = prev.filter(m => !existingTexts.has(m.text));
            return [...newLocalMessages, ...history];
          });

          const latestProposal = [...history].reverse().find(m => m.type === MESSAGE_TYPES.PROPOSAL);
          if (latestProposal) {
            onProposalReceived?.({ receivedAt: Date.now() });
          }

          const hasArrangementCompleted = history.some(m => m.type === MESSAGE_TYPES.ARRANGEMENT_COMPLETED);
          if (hasArrangementCompleted) {
            setIsArrangementRequested(true);
          }

          const confirmedMessage = history.find(m => m.type === MESSAGE_TYPES.RESERVATION_INFO_CONFIRMED);
          if (confirmedMessage) {
            if (confirmedMessage.inputDisabled) setInputDisabled(true);
            if (confirmedMessage.proposalChangeDisabled) setProposalChangeDisabled(true);
          }
        }
      } catch (error) {
        if (!mounted) return;
        debugLogRef.current.logError('Failed to fetch messages', error);
        const message =
          error instanceof Error && error.message
            ? error.message
            : '履歴の取得に失敗しました。時間をおいて再度お試しください。';
        Notification.error(message);
      }
    };
    loadMessages();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tripId, onProposalReceived]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    appendScript('https://js.pusher.com/3.0/pusher.min.js');
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let setupTimer: number | undefined;
    let cancelled = false;

    const subscribe = () => {
      if (cancelled) {
        return;
      }

      if (typeof Pusher === 'undefined') {
        setupTimer = window.setTimeout(subscribe, 300);
        return;
      }

      const channelName = buildTripAiChannelName(pusherChannelPrefix, tripId);
      pusherRef.current = new Pusher(pusherKey, { encrypted: true });
      channelRef.current = pusherRef.current.subscribe(channelName);
      const handler = (payload: PusherPayload) => {
        if (cancelled) return;
        debugLogRef.current.logPusher(payload.type || 'message', payload);

        const scrollAfterMessage = () => {
          onScrollToBottom?.();
        };

        if (payload.type === MESSAGE_TYPES.PROPOSAL) {
          onProposalReceived?.({ receivedAt: Date.now() });
          const proposalMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, proposalMessage];
          });
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.SUMMARY) {
          const summaryMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, summaryMessage];
          });
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT) {
          const insightMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, insightMessage];
          });
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.UNSUPPORTED_REQUEST) {
          const unsupportedRequestMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, unsupportedRequestMessage];
          });
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.RESERVATION_INFO_FORM) {
          onReservationFormReceived();

          const formMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOldForm = prev.filter(m => m.type !== MESSAGE_TYPES.RESERVATION_INFO_FORM);
            return [...withoutOldForm, formMessage];
          });
          if (payload.editable === false) {
            setMessages(prev =>
              prev.map(m => (m.type === MESSAGE_TYPES.RESERVATION_INFO_FORM ? { ...m, editable: false } : m))
            );
          }
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION) {
          const confirmationMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, confirmationMessage];
          });
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.ARRANGEMENT_COMPLETED) {
          const completedMessage = toChatMessage(payload, { isNew: true, isNewlyCompleted: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, completedMessage];
          });
          setIsArrangementRequested(true);
          scrollAfterMessage();
          return;
        }

        if (payload.type === MESSAGE_TYPES.RESERVATION_INFO_CONFIRMED) {
          setInputDisabled(true);
          setProposalChangeDisabled(true);

          createArrangementConfirmation(tripId).catch(error => {
            // eslint-disable-next-line no-console
            console.error('Failed to create arrangement confirmation:', error);
          });

          const confirmedMessage = toChatMessage(payload, { isNew: true });

          setMessages(prev => {
            const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
            return [...withoutOptimistic, confirmedMessage];
          });
          scrollAfterMessage();
          return;
        }

        const message = toChatMessage(payload, { isNew: true });

        setMessages(prev => {
          const withoutOptimistic = prev.filter(m => m.type !== MESSAGE_TYPES.OPTIMISTIC);
          return [...withoutOptimistic, message];
        });
        scrollAfterMessage();
      };
      bindHandlerRef.current = handler;
      channelRef.current.bind(tripAiMessageEventName, handler);
    };

    if (pusherKey) {
      subscribe();
    }

    return () => {
      cancelled = true;
      if (setupTimer) {
        window.clearTimeout(setupTimer);
      }
      if (channelRef.current && bindHandlerRef.current) {
        channelRef.current.unbind(tripAiMessageEventName, bindHandlerRef.current);
        const subscribedName = channelRef.current.name;
        channelRef.current = null;
        if (pusherRef.current) {
          pusherRef.current.unsubscribe(subscribedName);
        }
      }
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    onProposalReceived,
    onReservationFormReceived,
    onScrollToBottom,
    pusherChannelPrefix,
    pusherKey,
    tripId
  ]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const sendText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) {
        return;
      }

      const recentHistory = buildRecentMessagesPayload(messages, contextLength);

      const existing = messages.find(message => message.direction === 'user' && message.text === trimmed);
      const tempId = existing ? existing.id : `user-${Date.now()}`;
      const userMessage: ChatMessage = existing || {
        id: tempId,
        direction: 'user',
        text: trimmed,
        createdAt: new Date().toISOString(),
        isNew: true,
        feedback: null
      };

      if (!existing) {
        setMessages(prev => [...prev, userMessage]);
        onScrollToBottom?.();
      }
      setInputValue('');
      setSending(true);
      const startTime = Date.now();
      try {
        debugLogRef.current.logMessage('sent', trimmed);
        const savedMessage = await sendAiMessage(tripId, trimmed, recentHistory);
        debugLogRef.current.logApi('POST', `/trips/${tripId}/ai_messages`, {
          status: 200,
          duration: Date.now() - startTime,
          response: { messageId: savedMessage.id }
        });
        setMessages(prev =>
          prev.map(message => (message.id === tempId ? { ...savedMessage, isNew: message.isNew } : message))
        );
      } catch (e) {
        debugLogRef.current.logError('Failed to send message', e);
        const errorMessage =
          e instanceof Error && e.message ? e.message : '送信に失敗しました。時間をおいてもう一度お試しください。';
        Notification.error(errorMessage);
        if (!existing) {
          setMessages(prev => prev.filter(m => m.id !== tempId));
        }
      } finally {
        setSending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextLength, messages, onScrollToBottom, sending, tripId]
  );

  const handleSend = useCallback(async () => {
    if (!enabled) {
      return;
    }

    await sendText(inputValue);
  }, [enabled, inputValue, sendText]);

  const canSend = useMemo(
    () => enabled && !inputDisabled && inputValue.trim().length > 0 && !sending,
    [enabled, inputDisabled, inputValue, sending]
  );

  const isReservationInfoFormEditable = useMemo(() => {
    if (isArrangementRequested) {
      return false;
    }
    const reservationInfoFormMessage = messages.find(m => m.type === MESSAGE_TYPES.RESERVATION_INFO_FORM);
    const isFormSubmitted = reservationInfoFormMessage?.editable === false;
    return isFormSubmitted;
  }, [messages, isArrangementRequested]);

  const disableReservationInfoForm = useCallback(() => {
    setMessages(prev =>
      prev.map(message =>
        message.type === MESSAGE_TYPES.RESERVATION_INFO_FORM ? { ...message, editable: false } : message
      )
    );
  }, []);

  const handleArrangementRequest = useCallback(async (): Promise<boolean> => {
    if (isRequestingArrangement || isArrangementRequested) {
      return false;
    }

    setIsRequestingArrangement(true);
    try {
      const result = await createArrangement(tripId);
      if (result.success) {
        setIsArrangementRequested(true);
        return true;
      }
      Notification.error(result.error || '手配依頼に失敗しました。');
      return false;
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : '手配依頼に失敗しました。時間をおいて再度お試しください。';
      Notification.error(message);
      return false;
    } finally {
      setIsRequestingArrangement(false);
    }
  }, [isArrangementRequested, isRequestingArrangement, tripId]);
  return {
    messages,
    inputValue,
    sending,
    canSend,
    handleInputChange,
    handleSend,
    contextLength,
    setContextLength,
    disableReservationInfoForm,
    handleArrangementRequest,
    isRequestingArrangement,
    isArrangementRequested,
    inputDisabled,
    proposalChangeDisabled,
    isReservationInfoFormEditable
  };
};

const buildRecentMessagesPayload = (history: ChatMessage[], limit: number): RecentMessagePayload[] => {
  if (limit <= 0) {
    return [];
  }
  const startIndex = Math.max(history.length - limit, 0);
  return history.slice(startIndex).map(message => ({ direction: message.direction, text: message.text }));
};
